import React, { useEffect, useMemo, useState } from "react";
import classes from './GeneralLedgerStatement.module.css';
import HeaderNav from '../../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { BASE_URL } from "../../../Components/API/Api";
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';
import Footer from "../../../Components/Footer/Footer";

const GeneralLedgerStatement = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const token = localStorage.getItem("authToken");

    // UI state for filters and results
    const [accounts, setAccounts] = useState([]);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null); // contains { value: gl_code, label: gl_name }
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rows, setRows] = useState([]);
    const [noTransactions, setNoTransactions] = useState(false);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [closingBalance, setClosingBalance] = useState(0);
    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    // Fetch accounts for account search select (label: gl_name)
    useEffect(() => {
        const fetchAccounts = async () => {
            setAccountsLoading(true);
            try {
                const res = await axios.get(`${BASE_URL}/v1/account`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                });
                const payload = res?.data;
                let raw = payload?.data ?? payload;
                if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
                    raw = raw.data;
                }
                setAccounts(Array.isArray(raw) ? raw : []);
            } catch (err) {
                setAccounts([]);
            } finally {
                setAccountsLoading(false);
            }
        };
        if (token) fetchAccounts();
    }, [token]);

    const accountOptions = useMemo(() => {
        return (accounts || []).map(acc => ({
            value: acc?.id,
            label: acc?.gl_name,
            code: acc?.gl_code,
        })).filter(opt => (opt.value !== undefined && opt.value !== null) && opt.label);
    }, [accounts]);

    const handleRunReport = async (e) => {
        e.preventDefault();
        if (!selectedAccount?.value || !startDate || !endDate) {
            // minimal inline validation; could be improved with UI hints
            return;
        }
        setIsSubmitting(true);
        try {
            const toNumber = (val) => (typeof val === 'number' ? val : parseFloat(val) || 0);
            const dateOnly = (val) => {
                if (!val) return '';
                const s = String(val);
                if (s.includes('T')) return s.split('T')[0];
                return s.length >= 10 ? s.slice(0, 10) : s;
            };

            const buildRowsFromPayload = (payload) => {
                let tableRowsLocal = [];
                let obLocal = 0;
                let cbLocal = 0;
                let hasParents = false;

                if (payload && Array.isArray(payload.journal)) {
                    hasParents = true;
                    const parents = payload.journal;
                    parents.forEach((parent) => {
                        const parentAccountName = parent?.gl_name || parent?.account_name || parent?.account || '';
                        const lines = Array.isArray(parent?.journals) ? parent.journals : [];
                        lines.forEach((line) => {
                            tableRowsLocal.push({
                                date: dateOnly(line?.transaction_date || line?.date || line?.created_at),
                                description: line?.details || line?.description || line?.narration || line?.uuid || '',
                                accountName: parentAccountName,
                                debit: toNumber(line?.debit ?? line?.dr ?? line?.amount_debit ?? 0),
                                credit: toNumber(line?.credit ?? line?.cr ?? line?.amount_credit ?? 0),
                                balance: toNumber(line?.balance ?? line?.running_balance ?? 0),
                            });
                        });
                    });

                    if (parents.length > 0) {
                        const head = parents[0];
                        obLocal = toNumber(head?.opening_balance ?? 0);
                        cbLocal = toNumber(head?.closing_balance ?? head?.balance ?? 0);
                    }
                } else if (Array.isArray(payload)) {
                    tableRowsLocal = payload.map((r) => ({
                        date: dateOnly(r?.date || r?.transaction_date || r?.created_at),
                        description: r?.description || r?.gl_code || r?.narration || '',
                        accountName: r?.gl_name || r?.account_name || r?.account || '',
                        debit: toNumber(r?.debit ?? r?.dr ?? r?.amount_debit ?? 0),
                        credit: toNumber(r?.credit ?? r?.cr ?? r?.amount_credit ?? 0),
                        balance: toNumber(r?.closing_balance ?? r?.balance ?? r?.running_balance ?? 0),
                    }));

                    if (payload.length > 0) {
                        const head = payload[0];
                        obLocal = toNumber(head?.opening_balance ?? 0);
                        cbLocal = toNumber(head?.closing_balance ?? head?.balance ?? 0);
                    }
                }

                // Sort by date ascending
                tableRowsLocal.sort((a, b) => {
                    const da = new Date(a.date).getTime() || 0;
                    const db = new Date(b.date).getTime() || 0;
                    return da - db;
                });

                return { tableRowsLocal, obLocal, cbLocal, hasParents };
            };

            const fetchForRange = async (from, to) => {
                const res = await axios.get(`${BASE_URL}/v1/reports/general-ledger-filter`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                    params: {
                        gl_code: selectedAccount.value,
                        start_date: from,
                        end_date: to,
                    },
                });
                return res?.data?.data;
            };

            // First attempt: user-provided range
            const payload1 = await fetchForRange(startDate, endDate);
            const { tableRowsLocal: rows1, obLocal: ob1, cbLocal: cb1, hasParents: hasParents1 } = buildRowsFromPayload(payload1);

            if (rows1.length > 0) {
                setRows(rows1);
                setOpeningBalance(ob1);
                setClosingBalance(cb1);
                setNoTransactions(false);
            } else {
                // Fallback: widen start date far back to include historical transactions up to endDate
                const early = '1900-01-01';
                const payload2 = await fetchForRange(early, endDate);
                const { tableRowsLocal: rows2, obLocal: ob2, cbLocal: cb2 } = buildRowsFromPayload(payload2);

                if (rows2.length > 0) {
                    setRows(rows2);
                    setOpeningBalance(ob2);
                    setClosingBalance(cb2);
                    setNoTransactions(false);
                } else {
                    setRows([]);
                    setOpeningBalance(0);
                    setClosingBalance(0);
                    // If parent exists but truly no lines, surface no-transactions state
                    const hasAnyParents = (payload1 && Array.isArray(payload1.journal) && payload1.journal.length > 0) ||
                        (payload2 && Array.isArray(payload2.journal) && payload2.journal.length > 0);
                    setNoTransactions(hasAnyParents || true);
                }
            }
        } catch (err) {
            // You can surface specific error messages here if needed
            console.error("General ledger filter error:", err?.response?.data || err?.message);
            setRows([]);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns}>New Sales Invoice</button>
                    <button className={classes.btns}>New Purchase Invoice</button>
                    <button className={classes.btns}>All Invoices</button>
                    <button className={`${classes.btns} ${classes.greens}`}>Product & Services</button>
                </div>

               

                {/* Back Button */}
                <button className={classes.backButton} onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} style={{ marginRight: '6px' }} />
                    Back to report list
                </button>
                
 {/* Form and Icons */}
                <div className={classes.act}>
                    <form className={classes.form} onSubmit={handleRunReport}>
                        <div className={classes.group}>
                            <label>Account:</label>
                            <div className={classes.dropdownWrapper}>
                                <Select
                                    classNamePrefix="react-select"
                                    className={classes.statusDropdown}
                                    isLoading={accountsLoading}
                                    options={accountOptions}
                                    value={selectedAccount}
                                    onChange={(opt) => setSelectedAccount(opt)}
                                    placeholder="Search account by name"
                                    noOptionsMessage={() => accountsLoading ? 'Loading accounts...' : 'No accounts found'}
                                />
                            </div>
                        </div>
                        <div className={classes.grouppy}>
                            <label>Start Date:</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className={classes.grouppy}>
                            <label>End Date:</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className={classes.group1}>
                            <button className={classes.greenBtn} type="submit" disabled={isSubmitting}>
                                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                                {isSubmitting ? 'Running...' : 'Run Report'}
                            </button>
                        </div>
                    </form>

                    <div className={classes.iconbars}>
                        <div className={classes.iconbarr}>
                            <div className={classes.iconbar}>
                                <Filter size={16} style={{ marginRight: "6px" }} />
                                Filter
                            </div>
                            <div className={classes.iconbar}>
                                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                                Schedule
                            </div>
                        </div>

                       
                    </div>
                </div>
                {/* Report Content */}
                <div id="print-section" className={classes.containere}>
                <div className={classes.iconbarss}>
                            <div className={classes.iconbar}>
                                <Mail size={16} />
                            </div>
                            <div className={classes.iconbar} onClick={() => window.print()} style={{ cursor: 'pointer' }}>
                                <Printer size={16} />
                            </div>
                            <div className={classes.iconbar}>
                                <Download size={16} />
                            </div>
                            <div className={classes.iconbar}>
                                <Settings size={16} />
                            </div>
                        </div>
                    <header className={classes.headers}>
                        <h1 className={classes.companyName}>Promix Accounting Ltd</h1>
                        <h2 className={classes.title}>General Ledger</h2>
                        <p className={classes.period}>For the period ended January 31, 2025</p>
                        <button className={classes.unaudited}>unaudited</button>
                    </header>

                    <div className={classes.divider}></div>

                    <section className={classes.section}>
                        <div className={classes.row}>
                            <p className={classes.label}> Opening Balance </p>
                            <p className={classes.amount}>{Number(openingBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                    </section>

                    <div className={classes.tableContainer}>
                        <table className={classes.table}>
                            <thead>
                                <tr className={classes.tableHeader}>
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th>Account Name</th>
                                    <th>Debit	</th>
                                    <th>Credit</th>
                                    <th>Balance</th>

                                </tr>
                            </thead>
                            <tbody>
                                {rows && rows.length > 0 ? (
                                    rows.map((row, index) => (
                                        <tr key={index} className={classes.tableRow}>
                                            <td className={classes.tableCell}>{row.date}</td>
                                            <td style={{ width: '40%' }} className={classes.tableCell}>{row.description}</td>
                                            <td className={classes.tableCell}>{row.accountName}</td>
                                            <td style={{ color: '#1ba94c', textAlign: 'right' }} className={classes.tableCell}>{Number(row.debit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                            <td style={{ color: 'red', textAlign: 'right' }} className={classes.tableCell3}>{Number(row.credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                            <td className={classes.tableCell} style={{ textAlign: 'right' }}>{Number(row.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                        </tr>
                                    ))
                                ) : noTransactions ? (
                                    <tr className={classes.tableRow}>
                                        <td className={classes.tableCell} colSpan={6} style={{ textAlign: 'center' }}>No transactions for the selected period.</td>
                                    </tr>
                                ) : (
                                    <tr className={classes.tableRow}>
                                        <td className={classes.tableCell} colSpan={6} style={{ textAlign: 'center' }}>No data. Run a report to see results.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className={classes.netProfitRow}>
                        <p className={classes.netProfitLabel}>Closing Balance</p>
                        <p className={classes.netProfitAmount}>{Number(closingBalance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                    </div>
                </div>

                {/* <div className={classes.entrjj}>
                    <div className={classes.allkads}>
                        <div className={classes.thrdkad}>
                            <div className={classes.kad}>
                                <div className={classes.kdpt}>
                                    <p className={classes.kdptg1}>#46,700.00</p>
                                </div>
                                <div className={classes.rvnue}>
                                    <p className={classes.skaltxt}>Opening Balance</p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.frtkad}>
                            <div className={classes.kad}>
                                <div className={classes.kdpt}>
                                    <p className={classes.kdptg2}>#147,800.00</p>
                                </div>
                                <div className={classes.rvnue1}>
                                    <p className={classes.skaltxt}>Total Receipt</p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.frstkad2}>
                            <div className={classes.kad2}>
                                <div className={classes.kdpt}>
                                    <p className={classes.kdptg5}>#3,300,000.00</p>
                                </div>
                                <div className={classes.rvnue2}>
                                    <p className={classes.skaltxt5}>Total Payment</p>
                                </div>
                            </div>
                        </div>
                        <div className={classes.frstkad}>
                            <div className={classes.kad}>
                                <div className={classes.kdpt}>
                                    <p className={classes.kdptg4}>#31,200.00</p>
                                </div>
                                <div className={classes.rvnue2}>
                                    <p className={classes.skaltxt}>Opening Balance</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> */}


            </div>
            <Footer />
        </div>
    );
};

export default GeneralLedgerStatement;
